import { QueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { OneRosterAuth } from '@/components/auth/auth.context';
import { useMapTestRedirect } from '@/features/placements-v2/hooks/useMapTestRedirect';
import { UserRecommendations } from '@/features/recommendations/components/UserRecommendations';
import { userEnrollmentsQueryOptions } from '@/features/recommendations/queries/enrollments';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { getImpersonationStatus } from '@/hooks/useImpersonation';
import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';
import { oneRosterUserQueryOptions } from '@/hooks/useOneRosterAuthQuery';
import { UserRoles } from '@/lib/constants/auth.constants.ts';

const appSearchParams = z.object({
  courseId: z.string().optional(),
  schoolId: z.string().optional(),
});

export const Route = createFileRoute('/_app/app/')({
  component: AppHomeRoute,
  validateSearch: zodValidator(appSearchParams),
  ssr: false,
  loader: async ({ context }) => {
    const queryClient = context.queryClient as QueryClient;
    const queryOptions = oneRosterUserQueryOptions();

    await queryClient.prefetchQuery(queryOptions);
    const auth = queryClient.getQueryData(queryOptions.queryKey);

    if (auth?.user?.sourcedId) {
      const userSourcedId = auth.user.sourcedId;
      // prefetch enrollments
      await queryClient.prefetchQuery(
        userEnrollmentsQueryOptions(userSourcedId)
      );
    }
  },
  beforeLoad: async ({ context }) => {
    const auth = context.auth as OneRosterAuth;
    const { isImpersonated } = await getImpersonationStatus();

    const roles = auth?.user?.roles.map((role) => role.role) ?? [];
    const isAide = roles.includes(UserRoles.AIDE);
    const isStudent = roles.includes(UserRoles.STUDENT);
    const isAdmin =
      roles.includes(UserRoles.ADMINISTRATOR) ||
      roles.includes(UserRoles.SYSTEM_ADMINISTRATOR);

    const rolesToRedirect = [UserRoles.PARENT];
    if (isAide && !isImpersonated) {
      rolesToRedirect.push(UserRoles.AIDE);
    }

    if (
      !isAdmin &&
      !isStudent &&
      roles.some((role) => rolesToRedirect.includes(role as UserRoles))
    ) {
      throw redirect({ to: '/app/my-learning-report', replace: true });
    }
  },
});

function AppHomeRoute() {
  const auth = useOneRosterAuth();
  const user = auth.user;
  useMapTestRedirect(user);

  return (
    <AppPageContent>
      <AppPageHeader
        title="Home"
        center={
          <div className="text-lg font-bold whitespace-nowrap hidden md:block">
            Welcome, {user?.givenName} {user?.familyName}!
          </div>
        }
      />
      <UserRecommendations />
    </AppPageContent>
  );
}
